// src/i18n/es/views/dominioPropio.js
//
// Guía que ve el anfitrión para apuntar su dominio a la web que se ha montado en
// el constructor.
//
// Está escrita para alguien que no sabe qué es un DNS: cada paso es una frase y
// una acción. Los avisos van ANTES del paso que pueden estropear y no después,
// porque después ya ha fallado la verificación y el anfitrión abre un ticket.
//
// El grueso son las instrucciones de Cloudflare: es donde están los dominios de
// nuestros clientes. Los rótulos van en español y en inglés entre comillas porque
// Cloudflare traduce el panel y no sabemos con cuál de los dos se lo encontrará.
//
// La nube naranja tiene bloque propio a propósito. Cloudflare la activa por
// defecto y, con ella puesta, una consulta al dominio devuelve las direcciones de
// Cloudflare en vez del destino que pedimos: la comprobación no ve nada y el
// certificado no se puede emitir. Es la causa de casi todos los fallos.
//
// Marcadores que rellena la pantalla:
//   {dominio}    lo que ha escrito el anfitrión — midominio.com
//   {destino}    host al que tiene que apuntar el CNAME — lo da la API
//   {nombre}     lo que va en el campo «Nombre» del registro — @, www, reservas…
//   {tipo}       CNAME o TXT
//   {valor}      contenido del registro
//   {encontrado} lo que hemos encontrado al comprobar, cuando no cuadra
//   {actual}     dirección provisional de WACheckin — mialojamiento.wacheckin.site
//   {sitio}      nombre de la web ya conectada, en los choques dentro de la cuenta
//   {panel}      dónde se hacen los apuntes: «Cloudflare» o «el panel de tu dominio»
//   {panelCorto} lo mismo, corto para el indicador de pasos: «Cloudflare» o «tu dominio»

export const dominioPropio = {
  cabecera: {
    titulo: "Conecta tu propio dominio",
    subtitulo:
      "Tu web se ve ahora en {actual}. Al terminar se verá en tu dirección de siempre.",
    duracion: "Diez minutos por tu parte. El resto lo hace internet solo.",
  },

  // Los tres pasos del asistente. El anfitrión sale del panel de WACheckin en el
  // paso 2 y vuelve en el 3, así que cada rótulo dice dónde está.
  pasos: {
    uno: "Tu dominio",
    // {panelCorto}: «Cloudflare» o «tu dominio», según dónde lo tenga.
    dos: "Los dos apuntes en {panelCorto}",
    tres: "Comprobar",
    unoDetalle: "Escribes la dirección que quieres usar",
    dosDetalle: "Copias dos líneas en el panel de tu dominio",
    tresDetalle: "Nosotros comprobamos que todo está en su sitio",
    indicador: "Paso {paso} de 3",
  },

  // ── Paso 1: escribir el dominio ────────────────────────────────────────────
  paso1: {
    titulo: "¿Qué dirección quieres para tu web?",
    descripcion:
      "Escribe el dominio que ya tienes comprado. Nosotros no vendemos dominios: esto conecta uno que ya es tuyo.",
    etiqueta: "Tu dominio",
    marcador: "midominio.com",
    ayuda: "Escríbelo tal cual, sin «https://» y sin barras al final.",

    // Se pregunta aquí porque cambia el registro que hay que crear y el aviso del
    // dominio raíz. Preguntarlo después obliga a rehacer el paso 2.
    formaTitulo: "¿Y cómo quieres que se lea?",
    formaRaiz: {
      titulo: "midominio.com",
      descripcion: "La dirección corta, sin nada delante. Es la más habitual.",
    },
    formaWww: {
      titulo: "www.midominio.com",
      descripcion: "La de toda la vida, con «www» delante.",
    },
    formaSubdominio: {
      titulo: "reservas.midominio.com",
      descripcion:
        "Una dirección aparte, para no tocar la web que ya tienes en {dominio}.",
    },
    subdominioEtiqueta: "La palabra que va delante",
    subdominioMarcador: "reservas",
    subdominioAyuda: "Solo la palabra. El resto lo ponemos nosotros.",

    avisoWebExistente: {
      titulo: "¿Ya tienes una web en esa dirección?",
      texto:
        "Si {dominio} enseña hoy otra web, al terminar dejará de verse y se verá esta. Si prefieres conservarla, usa una dirección aparte como reservas.{dominio}.",
    },

    boton: "Continuar",
    botonComprobando: "Comprobando el dominio…",
  },

  // ── Dónde tiene el dominio ─────────────────────────────────────────────────
  proveedor: {
    pregunta: "¿Dónde compraste o gestionas {dominio}?",
    ayuda:
      "Es la empresa donde pagas la renovación cada año. Si no lo recuerdas, mira en tu correo la última factura del dominio.",
    cloudflare: "Cloudflare",
    cloudflareDetalle: "Instrucciones completas, pantalla por pantalla",
    otro: "Otra empresa",
    otroDetalle: "GoDaddy, IONOS, Namecheap, Dinahosting…",
    otroTexto:
      "Los apuntes son exactamente los mismos. Solo cambia dónde está el botón. Busca en tu panel algo que ponga «DNS», «Zona DNS» o «Gestión de DNS» y copia ahí las líneas de la tabla de arriba, una por una.",
    noLoSe: "No lo sé",
    noLoSeTexto:
      "Escríbenos con tu dominio y te decimos dónde está y qué hay que tocar.",
  },

  // ── Paso 2: los registros ──────────────────────────────────────────────────
  paso2: {
    titulo: "Ahora hay que decirle a tu dominio dónde está tu web",
    descripcion:
      "Tu dominio es un nombre. Por sí solo no lleva a ningún sitio: alguien tiene que apuntar en una libreta que ese nombre corresponde a nuestro servidor. Esa libreta se llama DNS y está en el panel de tu dominio.",

    queEsUnRegistro: {
      titulo: "Qué vas a hacer exactamente",
      texto:
        "Vas a añadir una línea a esa libreta. Una sola. Tiene tres huecos: cómo se llama, a dónde apunta y si pasa o no por un filtro. Nada más.",
      textoOtro:
        "Vas a añadir líneas a esa libreta. Cada una tiene dos huecos: cómo se llama y a dónde apunta. Nada más.",
    },

    copiaTitulo: "Copia estos datos tal cual",
    copiaAyuda:
      "Usa los botones de copiar. Un espacio de más o una letra cambiada y no funciona.",

    tabla: {
      tipo: "Tipo",
      nombre: "Nombre",
      destino: "Destino",
      contenido: "Contenido",
      proxy: "Estado del proxy",
      ttl: "TTL",
      valorProxy: "Solo DNS (nube gris)",
      valorTtl: "Auto",
    },

    copiar: "Copiar",
    copiado: "Copiado",
    copiarTodo: "Copiar los datos",

    tipos: {
      cnameTitulo: "CNAME",
      cnameTexto:
        "Es un reenvío: dice que tu dominio se atiende en {destino}. Si algún día cambiamos de servidor, tú no tienes que tocar nada.",
      txtTitulo: "TXT",
      txtTexto:
        "Es una nota firmada. No lleva a ningún sitio: solo demuestra que el dominio es tuyo. Se puede borrar cuando la web ya funcione, pero es más cómodo dejarla puesta.",
    },

    // El campo «Nombre» es el segundo motivo de fallo, después del proxy. Nadie
    // adivina que el dominio raíz se escribe con una arroba.
    nombreTitulo: "Qué se escribe en «Nombre»",
    nombreRaiz:
      "Para el dominio corto ({dominio}) escribe una arroba: @. En la libreta del DNS la arroba significa «el dominio a secas, sin nada delante». No escribas {dominio} entero.",
    nombreWww: "Para www.{dominio} escribe solo: www",
    nombreSubdominio:
      "Para {nombre}.{dominio} escribe solo la palabra de delante: {nombre}",
    nombreAviso:
      "En Cloudflare escribes solo esa parte. Él añade «.{dominio}» por su cuenta y te lo enseña debajo del campo.",
    nombreAvisoOtro:
      "En casi todos los paneles se escribe solo esa parte y ellos añaden «.{dominio}». Si el tuyo te pide el nombre entero, escribe {nombre}.{dominio}.",
    nombreAvisoOtroRaiz:
      "Si tu panel no acepta «@», deja el nombre vacío: en muchos significa lo mismo, el dominio sin nada delante.",

    volver: "Ya lo he hecho, volver a WACheckin",
    ayudaEnviar: "Enviarme estos datos por correo",
    ayudaEnviarTexto:
      "Si lo va a hacer tu informático o quien te llevó la web, te mandamos las instrucciones a tu correo para que se las reenvíes.",
  },

  // ── Cloudflare, paso a paso ────────────────────────────────────────────────
  cloudflare: {
    titulo: "Hazlo en Cloudflare",
    subtitulo: "Diez minutos. Ten a mano el correo y la contraseña de Cloudflare.",

    pasos: {
      uno: "Entra en dash.cloudflare.com con tu cuenta.",
      dos: "En la primera pantalla salen tus dominios. Pulsa {dominio}.",
      tres:
        "En el menú de la izquierda abre «DNS» y luego «Registros» («Records»).",
      cuatro:
        "Verás una lista con las líneas que ya tienes. Pulsa el botón «Añadir registro» («Add record»).",
      cinco: "En «Tipo» («Type») elige {tipo} en el desplegable.",
      seis: "En «Nombre» («Name») escribe {nombre}",
      siete: "En «Destino» («Target») pega {valor}",
      ocho:
        "En «Estado del proxy» («Proxy status») pulsa la nube naranja. Tiene que quedarse gris y poner «Solo DNS» («DNS only»).",
      nueve: "Deja «TTL» en «Auto». No lo toques.",
      diez: "Pulsa «Guardar» («Save»).",
      once:
        "Si la tabla de arriba tiene más de una línea, repite desde el paso 4 con la siguiente.",
    },

    hecho: "Ya está. Vuelve a WACheckin y pulsa «Comprobar».",

    // Cloudflare presenta este ajuste como más seguro, así que hay que justificar
    // por qué le pedimos que lo quite o lo dejará puesto.
    avisoAntes: {
      titulo: "Antes de guardar, lee esto",
      texto:
        "Cloudflare pone esa nube en naranja él solo, sin preguntar. Si la dejas naranja, la conexión no funcionará. El paso 8 es el importante de toda esta pantalla.",
    },

    accesoNegado: {
      titulo: "¿No te deja entrar en Cloudflare?",
      texto:
        "La cuenta suele estar a nombre de quien te montó la web. Pídele que haga estos pasos o que te añada como miembro del dominio.",
    },
  },

  // ── La nube naranja ────────────────────────────────────────────────────────
  proxy: {
    titulo: "La nube naranja: esto es lo que falla siempre",
    queEs:
      "Junto a cada línea del DNS, Cloudflare enseña una nubecita. Naranja significa que el tráfico pasa antes por Cloudflare. Gris significa que va directo.",
    porQueFalla:
      "Con la nube naranja, cuando alguien pregunta por tu dominio Cloudflare no contesta {destino}: contesta con sus propias direcciones. Nuestra comprobación pregunta, ve a Cloudflare y no encuentra el apunte que le pedimos. Aunque tú lo hayas escrito bien, para nosotros es como si no existiera.",
    porQueFalla2:
      "Y hay una segunda consecuencia peor: el candado de seguridad de tu web se emite comprobando que el dominio llega hasta nosotros. Con la nube naranja no llega, así que no se puede emitir y el navegador avisará de que el sitio no es seguro.",
    comoSeArregla:
      "Pulsa la nube. Se pone gris y el texto de al lado cambia a «Solo DNS» («DNS only»). Guarda. Eso es todo.",
    grisEsSeguro:
      "Ponerla en gris no deja tu web desprotegida. Tu web la servimos nosotros, con su certificado y detrás de nuestra propia protección. Cloudflare seguía en medio solo por cómo venía configurado de fábrica.",
    ttlBloqueado:
      "Detalle por el que la gente se despista: mientras la nube está naranja, Cloudflare no te deja tocar el TTL. Al ponerla gris se desbloquea. Déjalo en «Auto».",
    etiquetaProxied: "Con proxy («Proxied») — nube naranja — NO",
    etiquetaSoloDns: "Solo DNS («DNS only») — nube gris — SÍ",
  },

  // ── El dominio corto (raíz) ────────────────────────────────────────────────
  raiz: {
    titulo: "Si usas el dominio corto: no tienes que hacer nada especial",
    texto:
      "Hay una regla vieja de internet que dice que el dominio corto ({dominio}, sin nada delante) no admite este tipo de apunte. Con la mayoría de las empresas eso obliga a dar rodeos.",
    flattening:
      "Cloudflare lo resuelve solo. Cuando el «Nombre» es @, aplica una cosa suya llamada CNAME flattening: guarda tu reenvío y, cuando alguien pregunta, contesta ya con la dirección final. Tú no tienes que activar nada, viene puesto en todos los planes, incluido el gratuito.",
    avisoAjusteGlobal:
      "Lo que sí conviene: no toques la opción «CNAME flattening for all CNAME records», en «DNS» → «Configuración» («Settings»). Aplicada a todo, deja de contestar los reenvíos tal cual están escritos y rompe las comprobaciones de propiedad de otros servicios que tengas: correo, Google, la tienda…",
    // El dominio corto en cualquier otra empresa: casi ninguna deja un CNAME ahí.
    tituloOtro: "Si usas el dominio corto ({dominio})",
    textoOtroA:
      "En el dominio sin nada delante van líneas de tipo A, las de la tabla. Las admiten todas las empresas.",
    textoOtroCname:
      "Muchas empresas no dejan poner un CNAME en el dominio sin nada delante. Si la tuya no te deja guardar esa línea, usa «www» o una dirección aparte como reservas.{dominio}, o escríbenos y lo resolvemos contigo.",
    conflicto: {
      titulo: "Si el dominio corto ya tenía algo puesto",
      texto:
        "Un mismo nombre no puede tener dos destinos a la vez. Si en la lista ya hay una línea de tipo A o CNAME con el «Nombre» en @ (o en «www», si vas a usar www), bórrala antes de añadir la nuestra. Cloudflare te avisará si no lo haces.",
      textoOtro:
        "Un mismo nombre no puede tener dos destinos a la vez. Si en tu panel ya hay una línea de tipo A o CNAME con ese mismo nombre, bórrala antes de añadir la nuestra.",
      comoBorrar:
        "Pásale el ratón por encima a esa línea, pulsa «Editar» («Edit») y luego «Eliminar» («Delete»).",
      apunta:
        "Apunta en un papel lo que ponía antes de borrarlo, por si hay que volver atrás.",
    },
  },

  // ── Cuánto tarda ───────────────────────────────────────────────────────────
  propagacion: {
    titulo: "¿Cuánto tarda en verse?",
    texto:
      "Cloudflare publica el cambio al momento. Lo que tarda es que el resto de internet se entere: cada ordenador se guarda una copia de la libreta durante un rato para no preguntar mil veces.",
    plazoNormal:
      "Con el TTL en «Auto», Cloudflare pide que esa copia dure cinco minutos. En la práctica, lo normal es que funcione entre cinco y treinta minutos después de guardar.",
    textoOtro:
      "Tu panel publica el cambio en unos minutos. Lo que tarda es que el resto de internet se entere: cada ordenador se guarda una copia de la libreta durante un rato para no preguntar mil veces.",
    plazoNormalOtro:
      "Lo normal es que funcione entre diez minutos y una hora después de guardar. Algunas empresas tardan más en publicar el cambio: cuenta con hasta 24 horas antes de preocuparte.",
    plazoMalo:
      "Si tu dominio ya enseñaba otra web, tu propio ordenador puede tardar más en soltar lo que tenía guardado. No es un fallo. Cuenta con 24 horas antes de preocuparte.",
    truco:
      "Truco para no volverte loco mirando: ábrelo en una ventana de incógnito, o desde el móvil con los datos en vez del wifi. Así ves lo que ve un cliente nuevo, no lo que tiene guardado tu ordenador.",
    dominioNuevo:
      "Aparte: si acabas de añadir el dominio a Cloudflare por primera vez, Cloudflare tarda hasta 24 horas en darlo por activo. Hasta entonces todo funciona como si las nubes estuvieran grises, aunque las veas naranjas. Espera a que el dominio ponga «Activo» («Active») antes de dar nada por malo.",
    ssl:
      "Cuando la comprobación salga bien, emitimos el candado de seguridad. Eso son unos minutos más. Hasta que termine, el navegador puede avisar de que el sitio no es seguro: es normal y se pasa solo.",
  },

  // ── Paso 3: comprobar ──────────────────────────────────────────────────────
  paso3: {
    titulo: "Vamos a comprobarlo",
    descripcion:
      "Miramos tu dominio y te decimos qué encontramos. Si algo no está, te decimos qué es y cómo se arregla.",
    boton: "Comprobar ahora",
    botonReintentar: "Volver a comprobar",
    comprobando: "Mirando tus registros…",
    comprobandoAyuda: "Tarda unos segundos.",

    resultado: {
      registroOk: "El apunte del DNS está puesto y apunta a nosotros",
      registroKo: "Todavía no vemos el apunte",
      proxyOk: "La nube está en gris, como tiene que estar",
      proxyKo: "La nube está en naranja",
      sslOk: "Candado de seguridad emitido",
      sslPendiente: "Emitiendo el candado de seguridad…",
      webOk: "Tu web abre en {dominio}, con candado",
      webActivando: "Estamos activando tu web en tu dominio, con su candado de seguridad",
      webDespues: "Después comprobamos que tu web abre, con candado",
    },

    // El DNS ya está bien y falta el certificado: no hay nada que tocar.
    activando: {
      titulo: "Tu dominio ya apunta bien. Estamos activando tu web",
      texto:
        "Solo falta el candado de seguridad, que se emite solo y suele tardar uno o dos minutos. No cambies nada en {panel}: lo comprobamos nosotros y aquí verás cuándo está.",
    },

    exito: {
      titulo: "Listo, tu web ya está en {dominio}",
      texto:
        "Puedes escribir la dirección en el navegador y verla. La dirección antigua {actual} sigue funcionando, así que los enlaces que ya habías repartido no se rompen.",
      verWeb: "Ver mi web",
      soloWww: "Tu web abre en la dirección con «www». Si quieres que también funcione sin «www», pide a tu proveedor de dominio una redirección a la dirección con «www», o un registro ALIAS/ANAME en la raíz si su panel lo permite.",
      copiarEnlace: "Copiar la dirección",
      siguiente: "Ahora ponla en tus perfiles de Booking, Airbnb, Instagram y en tu firma del correo.",
    },

    pendiente: {
      titulo: "Aún no está, y es lo normal",
      texto:
        "Si acabas de guardarlo en {panel}, dale unos minutos y vuelve a pulsar. No hace falta que te quedes aquí: te avisamos por correo en cuanto esté.",
      avisarme: "Avisarme por correo cuando esté",
    },
  },

  // ── Estados del dominio en la ficha ────────────────────────────────────────
  estados: {
    sinConectar: "Sin conectar",
    pendiente: "Esperando los apuntes",
    comprobando: "Comprobando",
    emitiendoSsl: "Emitiendo el candado",
    activo: "Funcionando",
    error: "Con un fallo",
    activoAyuda: "Tu web se ve en {dominio} y la conexión es segura.",
    activandoAyuda: "Tu dominio ya apunta bien. En unos minutos tu web abrirá en {dominio}, con candado.",
  },

  // ── Errores ────────────────────────────────────────────────────────────────
  // El texto dice qué ha pasado y qué hacer. Nunca solo lo primero.
  errores: {
    formatoInvalido: {
      titulo: "Eso no parece un dominio",
      texto: "Escríbelo así: midominio.com. Sin espacios y sin nada más.",
    },
    conProtocolo: {
      titulo: "Sobra el «https://»",
      texto: "Quítalo y deja solo el nombre: {dominio}",
    },
    conRuta: {
      titulo: "Sobra todo lo que va después de la barra",
      texto: "Deja solo la parte de delante: {dominio}",
    },
    sinExtension: {
      titulo: "Le falta la terminación",
      texto: "Un dominio termina en .com, .es, .net… Revisa que no se te haya quedado corto.",
    },
    dominioNoExiste: {
      titulo: "Ese dominio no existe",
      texto:
        "No hay nadie registrado con ese nombre. Repasa la ortografía. Si acabas de comprarlo, espera unas horas y vuelve a intentarlo.",
    },
    dominioCaducado: {
      titulo: "Ese dominio parece caducado",
      texto:
        "Entra en la empresa donde lo compraste y comprueba que la renovación está pagada. Mientras esté caducado no se puede conectar.",
    },
    yaUsadoOtraCuenta: {
      titulo: "Ese dominio ya está conectado a otra cuenta",
      texto:
        "Un dominio solo puede apuntar a un sitio a la vez. Si {dominio} es tuyo, escríbenos y lo desconectamos de la otra cuenta para que lo puedas usar aquí.",
      contactar: "Escribir a soporte",
    },
    yaUsadoEstaCuenta: {
      titulo: "Ya lo tienes conectado",
      texto:
        "{dominio} está en uso en «{sitio}». Desconéctalo de ahí o elige otra dirección para esta web.",
    },
    registrosNoAparecen: {
      titulo: "Todavía no vemos nada en tu dominio",
      texto:
        "Suele ser cuestión de minutos: acabas de guardarlo y aún no ha llegado a todas partes. Espera diez minutos y vuelve a pulsar «Comprobar».",
      repasa: "Si ya ha pasado media hora, repasa tres cosas en {panel}:",
      repasa1: "Que en «Nombre» pusiste {nombre} y no el dominio entero.",
      repasa2: "Que pulsaste «Guardar» («Save») y la línea aparece en la lista.",
      repasa3: "Que la escribiste en {dominio} y no en otro dominio de tu cuenta.",
    },
    proxyActivado: {
      titulo: "Está la nube naranja puesta",
      texto:
        "Vemos tu dominio, pero pasa por el proxy de Cloudflare y eso nos tapa el destino real. Mientras siga naranja, ni la comprobación ni el candado de seguridad pueden funcionar.",
      solucion:
        "Entra en «DNS» → «Registros», busca la línea con el nombre {nombre}, pulsa la nube naranja hasta que se ponga gris y ponga «Solo DNS» («DNS only»), y guarda. Luego vuelve y pulsa «Comprobar».",
    },
    apuntaOtroSitio: {
      titulo: "Tu dominio apunta a otro sitio",
      texto:
        "Encontramos {encontrado} y esperábamos {destino}. Casi siempre es un apunte antiguo de la web anterior que se quedó ahí.",
      solucion:
        "En «DNS» → «Registros», borra la línea vieja que tenga el nombre {nombre} y deja solo la nuestra.",
    },
    destinoAntiguo: {
      titulo: "El valor que te dimos antes no funcionaba",
      texto:
        "Tu apunte lleva a {encontrado}, que es lo que te indicábamos, pero esa dirección no funciona. El fallo fue nuestro, no tuyo.",
      solucion: "En {panel}, edita el apunte {nombre} y pon como valor {destino}. El TXT déjalo como está.",
    },
    registroDuplicado: {
      titulo: "Hay dos apuntes para el mismo nombre",
      texto:
        "Un nombre no puede llevar a dos sitios. Borra el que no sea el nuestro y vuelve a comprobar.",
    },
    error1014: {
      titulo: "Si el navegador enseña «Error 1014»",
      texto:
        "Es otra cara del mismo problema de siempre: la nube naranja. Ponla en gris («Solo DNS») y se va.",
    },
    sslFallido: {
      titulo: "No hemos podido emitir el candado de seguridad",
      texto:
        "El apunte del DNS está bien, pero el candado no sale. Suele ser la nube naranja otra vez, o un apunte antiguo que se nos cruza. Vuelve a intentarlo dentro de un rato; si sigue igual, escríbenos y lo miramos nosotros.",
      reintentar: "Reintentar la emisión",
    },
    sinPermisos: {
      titulo: "No puedes cambiar el dominio",
      texto: "Pídeselo al responsable de la cuenta.",
    },
    servidor: {
      titulo: "No hemos podido comprobarlo ahora mismo",
      texto: "Ha fallado algo por nuestro lado. Inténtalo en unos minutos.",
    },
    guardar: "No se ha podido guardar el dominio. Inténtalo otra vez.",
  },

  // ── Desconectar ────────────────────────────────────────────────────────────
  desconectar: {
    boton: "Desconectar este dominio",
    titulo: "¿Desconectar {dominio}?",
    texto:
      "Tu web dejará de verse en {dominio} y volverá a verse solo en {actual}. Quien escriba tu dominio no encontrará nada hasta que lo apuntes a otro sitio.",
    aviso: "Los apuntes que hiciste en {panel} siguen ahí. Bórralos tú si ya no los quieres.",
    cancelar: "Cancelar",
    confirmar: "Desconectar",
    hecho: "Dominio desconectado.",
  },

  // ── Dudas ──────────────────────────────────────────────────────────────────
  dudas: {
    titulo: "Dudas que salen siempre",

    correoPregunta: "¿Esto me va a tumbar el correo de @{dominio}?",
    correoTexto:
      "No. El correo va por otras líneas de la libreta, las de tipo MX, y no las tocamos. Mientras no borres esas, tu correo sigue igual.",

    otraWebPregunta: "Tengo otra web en ese dominio. ¿Qué pasa con ella?",
    otraWebTexto:
      "Deja de verse: el dominio pasa a enseñar esta. Si quieres conservar las dos, usa una dirección aparte como reservas.{dominio} para la de WACheckin.",

    volverAtrasPregunta: "¿Puedo volver atrás?",
    volverAtrasTexto:
      "Sí, cuando quieras. Se deshace donde se hizo: borras el apunte en {panel} y vuelves a dejar el que tenías. Tarda lo mismo en notarse, unos minutos.",

    wwwPregunta: "¿Funcionará con y sin «www»?",
    wwwTexto:
      "Sí, si pones las dos líneas que te damos. Una manda y la otra reenvía a la primera, para que Google no vea dos webs iguales.",

    costePregunta: "¿Me cuesta algo?",
    costeTexto:
      "Conectarlo, no. El dominio lo sigues pagando donde lo compraste, como hasta ahora.",

    quienPregunta: "No me veo capaz de hacer esto.",
    quienTexto:
      "Normal, es la parte más árida de todo el proceso. Escríbenos con tu dominio y lo dejamos hecho contigo por teléfono en diez minutos.",
  },

  // ── Botonera ───────────────────────────────────────────────────────────────
  botones: {
    atras: "Atrás",
    siguiente: "Siguiente",
    guardar: "Guardar",
    guardando: "Guardando…",
    cerrar: "Cerrar",
    entendido: "Entendido",
    abrirCloudflare: "Abrir Cloudflare",
    soporte: "Que me ayude WACheckin",
    verGuia: "Ver la guía otra vez",
  },

  avisos: {
    copiado: "Copiado al portapapeles",
    copiadoError: "No se ha podido copiar. Selecciónalo a mano.",
    guardado: "Dominio guardado. Ahora toca hacer los apuntes en {panel}.",
    conectado: "¡Tu web ya se ve en {dominio}!",
    correoEnviado: "Te hemos mandado las instrucciones a tu correo.",
  },
};
